import { useMemo } from 'react';

import { SITE_INVENTORY_SERVICES } from '@/constants';
import { useGetDistinctInventoryTypesBySite } from '@/dataAccess/rest';
import { useSiteContext } from '@/providers';
import type { SiteWithApis } from '@/stores';

type SiteInventorySupport = {
  /** Accessible sites that carry the service's inventory (site list ∩ per-site inventory types). */
  sites: SiteWithApis[];
  /** Ids of {@link sites}, for callers that only need membership tests. */
  siteIds: ReadonlySet<string>;
  /** True when the user can reach at least one site that supports the service. */
  hasSite: boolean;
  /** True when the selected site is in {@link sites}. False while the lookup is still loading or
   * when the hook is disabled (nothing fetched). */
  currentSiteIsSupported: boolean;
  /** True until the per-site type map AND the accessible-site list have both settled. */
  isInitialLoading: boolean;
};

/** Support state for every service in {@link SITE_INVENTORY_SERVICES}, keyed by service name. */
export type SiteServiceSupport = {
  readonly [K in keyof typeof SITE_INVENTORY_SERVICES]: SiteInventorySupport;
};

/**
 * Site support for every registered service: `const { orbitOne, penViewer } = useSiteServiceSupport()`.
 *
 * All services resolve from one cross-site query so every consumer shares a single cache entry.
 * Narrowing the query per route would not help: the sidebar (`useGetRouteTreeGroups`) needs the full
 * set on every page, so a narrower query would just add a second, redundant fetch.
 *
 * The inventory API only reports types that are usable at a site (deleted and deactivated items are
 * excluded), so a matching type is enough to call the service supported.
 *
 * Pass `enabled = false` to skip the network call where the answer is irrelevant (outside portable
 * mode); every service then reports no sites and `isInitialLoading: false`.
 */
export const useSiteServiceSupport = (enabled = true): SiteServiceSupport => {
  const { sites, currentSite, areSitesResolved } = useSiteContext();

  const { data: typesBySite, isFetched: areTypesResolved } =
    useGetDistinctInventoryTypesBySite(enabled);

  // Wait for both the type map and the accessible-site list, since the result is their
  // intersection; otherwise it is briefly empty while one source still loads.
  const isInitialLoading = enabled && !(areTypesResolved && areSitesResolved);
  const { id: currentSiteId } = currentSite ?? {};

  return useMemo(() => {
    const support = Object.entries(SITE_INVENTORY_SERVICES).map(([key, definition]) => {
      const serviceTypes = new Set(definition.types);
      // Driven off the accessible sites rather than the map's keys, so a site the user cannot reach
      // can never leak into the result.
      const supportedSites = sites.filter(site =>
        typesBySite?.[site.id]?.some(type => serviceTypes.has(type))
      );
      const siteIds = new Set(supportedSites.map(site => site.id));

      return [
        key,
        {
          sites: supportedSites,
          siteIds,
          hasSite: supportedSites.length > 0,
          currentSiteIsSupported: currentSiteId !== undefined && siteIds.has(currentSiteId),
          isInitialLoading,
        } satisfies SiteInventorySupport,
      ];
    });

    return Object.fromEntries(support) as SiteServiceSupport;
  }, [currentSiteId, isInitialLoading, sites, typesBySite]);
};
